import React, { useContext, useEffect, useState } from 'react';
import { Alert, Autocomplete, Box, Container, Grid, InputLabel, TextField } from '@mui/material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import ReactPhoneInput from 'react-phone-input-material-ui';

import { providerDetailsSchema } from 'utils/schema';
import { permissions } from 'utils/dropDownList';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { getProviderDetails, updateProviderDetails } from 'store/slices/userSlice';
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton';
import { toast } from 'react-toastify';
import { AuthContext, useAuth } from 'utils/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTE } from 'route/RouteEnums';
import { Restricted } from 'components/Restricted';
import { getOfficeSearchListByStatusAndType } from 'store/slices/secureMessageSlice';
import { IOfficeSearchDetails } from 'store/interfaces/IUserDetails';

const ProviderInfo = (props: any) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isDirty },
    setValue,
    reset,
    watch,
    clearErrors,
  } = useForm({
    resolver: yupResolver(providerDetailsSchema),
    mode: 'all',
  });
  const [searchOffices, setSearchOffices] = useState<Array<IOfficeSearchDetails>>([]);
  const isProfileDetailsFetched = useAppSelector(
    (state) => state.user.loaders.isProfileDetailsFetched
  );
  const [newOfficeId, setNewOfficeId] = useState<any>(null);
  const [disableEdit, setDisableEdit] = useState<boolean>(false);
  const { user } = useContext(AuthContext);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();

  const getProvider = () => {
    if (props?.from === 'admin' ? state?.patientid || props?.patientId : user?.patientId) {
      const data = {
        patientId: props?.from === 'admin' ? state?.patientid || props?.patientId : user?.patientId,
        type: 'provider',
      };
      dispatch(getProviderDetails(data))
        .unwrap()
        .then((response: any) => {
          if (response.status === 200) {
            const newOfficeStatus: boolean =
              response?.data?.officeStatus === 'Approved' ? true : false;
            setDisableEdit(newOfficeStatus);
            Object.keys(response.data).forEach((key: any) => {
              if (response.data[key] !== null) {
                setValue(key, response.data[key], { shouldValidate: true, shouldDirty: false });
              }
            });
          }
        });
    } else {
      const message = 'Please add patient details first.';
      toast(message, {
        type: 'info',
        icon: true,
      });
      navigate(ROUTE.PATIENT_INFORMATION);
    }
  };

  useEffect(() => {
    getOfficeSearch();
    getProvider();
    //eslint-disable-next-line
  }, []);

  const searchInput = (value: any) => {
    setValue('officeName', value, { shouldValidate: true, shouldDirty: true });
    if (searchOffices?.length === 0) {
      return;
    }
    if (value && value.length >= 3) {
      const matches = searchOffices.filter((office) =>
        office.officeName.toLowerCase().includes(value.toLowerCase())
      );
      if (matches?.length === 1 && matches[0]?.officeName.toLowerCase() === value.toLowerCase()) {
        setOfficeName(matches[0]);
      } else {
        setNewOfficeId(null);
        setDisableEdit(false);
      }
    } else {
      setNewOfficeId(null);
      setDisableEdit(false);
    }
  };

  const setOfficeName = (value: any) => {
    if (typeof value === 'object' && value !== null) {
      setNewOfficeId(value.id);
      setDisableEdit(value.status === 'Approved');
      setValue('officeName', value.officeName, { shouldValidate: true, shouldDirty: true });
      setValue('officePhoneNumber', value.officePhoneNumber, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue('officeFaxNumber', value.officeFaxNumber, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  };

  const getOfficeSearch = () => {
    if (props?.from === 'admin' ? state?.patientid || props?.patientId : user?.patientId) {
      const data = { status: 'Approved', type: 'Provider' };
      dispatch(getOfficeSearchListByStatusAndType(data))
        .unwrap()
        .then((response: any) => {
          if (response.status === 200) {
            setSearchOffices(response.data);
          }
        });
    }
  };

  const getPayload = (formData: any) => {
    const { officeName, firstName, lastName, officePhoneNumber, officeFaxNumber } = formData;
    return {
      newOfficeId: newOfficeId,
      officeName,
      firstName,
      lastName,
      officePhoneNumber: officePhoneNumber.slice(1),
      officeFaxNumber: officeFaxNumber !== '1' ? officeFaxNumber : '',
    };
  };

  const onSubmitForm = (formData: any) => {
    if (isValid && isDirty) {
      if (props?.from === 'admin' ? state?.patientid || props?.patientId : user?.patientId) {
        const payload = getPayload(formData);
        dispatch(
          updateProviderDetails({
            patientId:
              props?.from === 'admin' ? state?.patientid || props?.patientId : user?.patientId,
            payload,
            type: 'provider',
          })
        )
          .unwrap()
          .then((response: any) => {
            if (response.status === 200) {
              const message = 'Provider details updated successfully.';
              toast(message, {
                type: 'success',
                icon: true,
              });
              reset(formData);
              props?.onComplete();
            }
          });
      }
    } else {
      toast('No changes to update.', {
        type: 'info',
        icon: true,
      });
    }
  };

  return (
    <Container component='main' maxWidth='sm'>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box
          component='form'
          sx={{ mt: 1 }}
          onSubmit={handleSubmit((formData) => {
            onSubmitForm(formData);
          })}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Alert severity='info' sx={{ fontSize: '13px', lineHeight: '20px' }}>
                Start typing and select the correct provider from the list. If no match appears, add
                a new provider.
              </Alert>
            </Grid>
            <Grid item xs={12}>
              <InputLabel htmlFor='office-name'>Practice or Organization Name*</InputLabel>
              <Autocomplete
                freeSolo
                options={searchOffices}
                getOptionLabel={(option) =>
                  typeof option === 'string' ? option : option.officeName
                }
                onInputChange={(_, value) => searchInput(value)}
                onChange={(_, value) => {
                  setOfficeName(value);
                }}
                value={watch('officeName') ? watch('officeName') : null}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    fullWidth
                    placeholder="Enter provider's office name"
                    id='office-name'
                    autoComplete='officeName'
                    error={!!errors.officeName}
                    helperText={errors?.officeName?.message}
                    disabled={!user?.permissions.includes('EDIT_PROFILE')}
                    inputProps={{
                      ...params.inputProps,
                      maxLength: 255,
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <InputLabel htmlFor='last-name'>PCP Last Name*</InputLabel>
              <TextField
                required
                fullWidth
                placeholder="Enter provider's last name"
                id='lastName'
                autoComplete='lastName'
                {...register('lastName')}
                error={!!errors.lastName}
                helperText={errors?.lastName?.message}
                disabled={!user?.permissions.includes('EDIT_PROFILE')}
                inputProps={{
                  maxLength: 255,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <InputLabel htmlFor='firstName'>PCP First Name*</InputLabel>
              <TextField
                required
                fullWidth
                placeholder="Enter provider's first name"
                id='first-name'
                autoComplete='firstName'
                {...register('firstName')}
                error={!!errors.firstName}
                helperText={errors?.firstName?.message?.toString()}
                disabled={!user?.permissions.includes('EDIT_PROFILE')}
                inputProps={{
                  maxLength: 255,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <InputLabel htmlFor='phone'>
                {props?.from !== 'admin' ? 'Phone Number*' : 'Phone Number'}
              </InputLabel>
              <ReactPhoneInput
                component={TextField}
                label=''
                placeholder='+1 (555) 000-0000'
                onlyCountries={['us']}
                country={'us'}
                masks={{ us: '(...) ...-....' }}
                autoFormat={true}
                disableDropdown={true}
                defaultErrorMessage={'Please enter a phone number'}
                {...register('officePhoneNumber')}
                inputProps={{
                  helperText: errors.officePhoneNumber ? errors.officePhoneNumber?.message : '',
                  error: !!errors.officePhoneNumber,
                }}
                countryCodeEditable={false}
                onChange={(e) => {
                  setValue('officePhoneNumber', e, { shouldValidate: true, shouldDirty: true });
                }}
                value={watch('officePhoneNumber') ? watch('officePhoneNumber') : null}
                disabled={disableEdit || !user?.permissions.includes('EDIT_PROFILE')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <InputLabel htmlFor='faxNumber'>Fax Number</InputLabel>
              <ReactPhoneInput
                component={TextField}
                label=''
                placeholder='+1 (555) 000-0000'
                onlyCountries={['us']}
                country={'us'}
                masks={{ us: '(...) ...-....' }}
                autoFormat={true}
                disableDropdown={true}
                defaultErrorMessage={'Please enter a fax number'}
                {...register('officeFaxNumber')}
                inputProps={{
                  helperText: errors?.officeFaxNumber ? errors.officeFaxNumber?.message : '',
                  error: !!errors.officeFaxNumber,
                }}
                countryCodeEditable={false}
                onChange={(e) => {
                  setValue('officeFaxNumber', e, { shouldValidate: true, shouldDirty: true });
                }}
                value={watch('officeFaxNumber') ? watch('officeFaxNumber') : ''}
                onBlur={() => {
                  if (watch('officeFaxNumber') === '1') clearErrors('officeFaxNumber');
                }}
                disabled={disableEdit || !user?.permissions.includes('EDIT_PROFILE')}
              />
            </Grid>
            <Restricted permission={permissions.EDIT_PROFILE}>
              <Grid item xs={12} display={'flex'} justifyContent={'center'}>
                <LoadingButton
                  loading={isProfileDetailsFetched}
                  variant='contained'
                  // loadingPosition='start'
                  type='submit'
                  sx={{
                    minWidth: 140,
                    mt: 2,
                    mb: 2,
                  }}
                >
                  Save
                </LoadingButton>
              </Grid>
            </Restricted>
          </Grid>
        </Box>
      </Box>
    </Container>
  );
};

export default ProviderInfo;
